import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { 
  Users, FileText, Download, Upload, RefreshCw, 
  Briefcase, Calendar, FileSpreadsheet, Receipt, Eye
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { PageHeader } from '@/components/PageHeader';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ROLE_TYPES = {
  PORTIERE: { label: 'Portiere', color: 'bg-blue-100 text-blue-800' },
  GIARDINIERE: { label: 'Giardiniere', color: 'bg-green-100 text-green-800' },
  MANUTENTORE: { label: 'Manutentore', color: 'bg-orange-100 text-orange-800' },
  RSPP: { label: 'RSPP', color: 'bg-red-100 text-red-800' },
  ADDETTO_PULIZIE: { label: 'Addetto Pulizie', color: 'bg-purple-100 text-purple-800' },
  ALTRO: { label: 'Altro', color: 'bg-gray-100 text-gray-800' }
};

const F24_TYPES = [
  { value: 'ORDINARIO', label: 'Ordinario' },
  { value: 'ACCONTO', label: 'Acconto' },
  { value: 'SALDO', label: 'Saldo' }
];

export default function StaffConsultant() {
  const [loading, setLoading] = useState(true);
  const [staff, setStaff] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [payslipDialogOpen, setPayslipDialogOpen] = useState(false);
  const [f24DialogOpen, setF24DialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [payslips, setPayslips] = useState([]);
  const [f24Documents, setF24Documents] = useState([]);
  const { token, selectedCondominium, userRole } = useAuth();
  
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  
  const [payslipForm, setPayslipForm] = useState({
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
    file: null
  });
  
  const [f24Form, setF24Form] = useState({
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
    f24_type: 'ORDINARIO',
    description: '',
    file: null
  });

  const condoId = selectedCondominium?.condominium?.id;
  const isAdmin = userRole === 'ADMIN' || selectedCondominium?.role === 'ADMIN';
  const isConsultant = userRole === 'LABOR_CONSULTANT' || selectedCondominium?.role === 'LABOR_CONSULTANT';
  const canAccess = isAdmin || isConsultant;

  const fetchStaff = useCallback(async () => {
    if (!token || !condoId || !canAccess) return;
    setLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/api/condominiums/${condoId}/staff?status_filter=ACTIVE`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) {
        const data = await res.json();
        setStaff(data.staff || []);
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Errore caricamento personale');
    } finally {
      setLoading(false);
    }
  }, [token, condoId, canAccess]);

  const fetchPayslips = useCallback(async () => {
    if (!token || !condoId || !canAccess) return;
    try {
      const res = await fetch(
        `${API_URL}/api/condominiums/${condoId}/staff/payslips?year=${selectedYear}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) {
        const data = await res.json();
        setPayslips(data.documents || []);
      }
    } catch (error) {
      console.error('Error fetching payslips:', error);
    }
  }, [token, condoId, canAccess, selectedYear]);

  const fetchF24 = useCallback(async () => {
    if (!token || !condoId || !canAccess) return;
    try {
      const res = await fetch(
        `${API_URL}/api/condominiums/${condoId}/staff/f24?year=${selectedYear}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) {
        const data = await res.json();
        setF24Documents(data.documents || []);
      }
    } catch (error) {
      console.error('Error fetching F24:', error);
    }
  }, [token, condoId, canAccess, selectedYear]);

  useEffect(() => {
    fetchStaff();
    fetchPayslips();
    fetchF24();
  }, [fetchStaff, fetchPayslips, fetchF24]);

  const handleExportMonthly = async (format = 'PDF') => {
    if (!condoId) return;
    try {
      const res = await fetch(
        `${API_URL}/api/condominiums/${condoId}/staff/export/monthly?year=${selectedYear}&month=${selectedMonth}&format=${format}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `riepilogo_presenze_${selectedYear}_${selectedMonth.toString().padStart(2, '0')}.${format.toLowerCase()}`;
        a.click();
        window.URL.revokeObjectURL(url);
        toast.success('Export completato');
      } else {
        toast.error('Errore export');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    }
  };

  const handleUploadPayslip = async () => {
    if (!selectedStaff || !payslipForm.file) {
      toast.error('Seleziona un file');
      return;
    }
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('year', payslipForm.year.toString());
      formData.append('month', payslipForm.month.toString());
      formData.append('file', payslipForm.file);
      
      const res = await fetch(
        `${API_URL}/api/condominiums/${condoId}/staff/${selectedStaff.id}/payslips`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData
        }
      );
      
      if (res.ok) {
        toast.success('Busta paga caricata e inviata al dipendente');
        setPayslipDialogOpen(false);
        setPayslipForm({ year: new Date().getFullYear(), month: new Date().getMonth() + 1, file: null });
        setSelectedStaff(null);
        fetchPayslips();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore upload');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    } finally {
      setUploading(false);
    }
  };

  const handleUploadF24 = async () => {
    if (!f24Form.file) {
      toast.error('Seleziona un file');
      return;
    }
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('year', f24Form.year.toString());
      formData.append('month', f24Form.month.toString());
      formData.append('f24_type', f24Form.f24_type);
      formData.append('description', f24Form.description || '');
      formData.append('file', f24Form.file);
      
      const res = await fetch(
        `${API_URL}/api/condominiums/${condoId}/staff/f24`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData
        }
      );
      
      if (res.ok) {
        toast.success('F24 caricato con successo');
        setF24DialogOpen(false);
        setF24Form({ year: new Date().getFullYear(), month: new Date().getMonth() + 1, f24_type: 'ORDINARIO', description: '', file: null });
        fetchF24();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore upload');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    } finally {
      setUploading(false);
    }
  };

  const openPayslipDialog = (member) => {
    setSelectedStaff(member);
    setPayslipDialogOpen(true);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('it-IT');
  };

  const downloadDocument = (docId) => {
    window.open(`${API_URL}/api/documents/${docId}/download?token=${token}`, '_blank');
  };

  if (!canAccess) {
    return (
      <div className="container mx-auto px-4 py-6">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-8">
              <Briefcase className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
              <h2 className="text-xl font-semibold mb-2">Accesso non autorizzato</h2>
              <p className="text-muted-foreground">
                Questa sezione è riservata ad Amministratori e Consulenti del Lavoro.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <RefreshCw className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6 space-y-6">
      {/* Header */}
      <PageHeader
        title="Consulente del Lavoro"
        subtitle="Buste paga, F24 e presenze"
      />

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Dipendenti Attivi</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{staff.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Buste Paga {selectedYear}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{payslips.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">F24 {selectedYear}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{f24Documents.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Ruolo</CardTitle>
          </CardHeader>
          <CardContent>
            <Badge className="text-lg">
              {isAdmin ? 'ADMIN' : 'CONSULENTE'}
            </Badge>
          </CardContent>
        </Card>
      </div>

      {/* Period Selection */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Periodo di Riferimento
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Label>Mese:</Label>
              <Select value={selectedMonth.toString()} onValueChange={(v) => setSelectedMonth(parseInt(v))}>
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => (
                    <SelectItem key={m} value={m.toString()}>
                      {new Date(2025, m-1, 1).toLocaleDateString('it-IT', {month: 'long'})}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Label>Anno:</Label>
              <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
                <SelectTrigger className="w-28">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[2024, 2025, 2026].map(y => (
                    <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1" />
            <Button variant="outline" onClick={() => handleExportMonthly('CSV')}>
              <FileSpreadsheet className="h-4 w-4 mr-2" />
              Export CSV
            </Button>
            <Button onClick={() => handleExportMonthly('PDF')}>
              <Download className="h-4 w-4 mr-2" />
              Export PDF
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs defaultValue="staff" className="space-y-4">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="staff">
            <Users className="h-4 w-4 mr-2" />
            Dipendenti
          </TabsTrigger>
          <TabsTrigger value="payslips">
            <FileText className="h-4 w-4 mr-2" />
            Buste Paga
          </TabsTrigger>
          <TabsTrigger value="f24">
            <Receipt className="h-4 w-4 mr-2" />
            F24
          </TabsTrigger>
        </TabsList>

        {/* STAFF LIST */}
        <TabsContent value="staff">
          <Card>
            <CardHeader>
              <CardTitle>Personale dello Stabile</CardTitle>
              <CardDescription>Elenco dipendenti attivi</CardDescription>
            </CardHeader>
            <CardContent>
              {staff.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Dipendente</TableHead>
                      <TableHead>Ruolo</TableHead>
                      <TableHead>Contratto</TableHead>
                      <TableHead>Ore/Sett.</TableHead>
                      <TableHead>Data Assunzione</TableHead>
                      <TableHead className="text-right">Azioni</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {staff.map((member) => {
                      const roleInfo = ROLE_TYPES[member.role_type] || ROLE_TYPES.ALTRO;
                      return (
                        <TableRow key={member.id}>
                          <TableCell>
                            <div>
                              <p className="font-medium">{member.first_name} {member.last_name}</p>
                              <p className="text-xs text-muted-foreground">{member.fiscal_code || '-'}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge className={roleInfo.color}>{roleInfo.label}</Badge>
                          </TableCell>
                          <TableCell>{member.employment_type}</TableCell>
                          <TableCell>{member.weekly_hours}h</TableCell>
                          <TableCell>{formatDate(member.start_date)}</TableCell>
                          <TableCell className="text-right">
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => openPayslipDialog(member)}
                            >
                              <Upload className="h-4 w-4 mr-1" />
                              Busta Paga
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Nessun dipendente attivo</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* PAYSLIPS */}
        <TabsContent value="payslips">
          <Card>
            <CardHeader>
              <CardTitle>Buste Paga {selectedYear}</CardTitle>
              <CardDescription>
                Le buste paga sono visibili ai dipendenti e all'amministratore.
                Viene inviata una notifica automatica al dipendente al caricamento.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {payslips.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Dipendente</TableHead>
                      <TableHead>Periodo</TableHead>
                      <TableHead>File</TableHead>
                      <TableHead>Caricato il</TableHead>
                      <TableHead className="text-right">Azioni</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {payslips.map((doc) => (
                      <TableRow key={doc.id}>
                        <TableCell className="font-medium">{doc.title?.split(' - ')[1] || '-'}</TableCell>
                        <TableCell>
                          <Badge variant="outline">
                            {doc.payslip_month?.toString().padStart(2, '0')}/{doc.payslip_year}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">{doc.file_name}</TableCell>
                        <TableCell>{formatDate(doc.created_at)}</TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="sm" onClick={() => downloadDocument(doc.id)}>
                            <Eye className="h-4 w-4 mr-1" />
                            Visualizza
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Nessuna busta paga per il {selectedYear}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* F24 */}
        <TabsContent value="f24">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>F24 Oneri Personale {selectedYear}</CardTitle>
                <CardDescription>
                  Modelli F24 relativi agli oneri del personale.
                  Visibili solo ad amministratore e consulente.
                </CardDescription>
              </div>
              <Button onClick={() => setF24DialogOpen(true)}>
                <Upload className="h-4 w-4 mr-2" />
                Carica F24
              </Button>
            </CardHeader>
            <CardContent>
              {f24Documents.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Periodo</TableHead>
                      <TableHead>Descrizione</TableHead>
                      <TableHead>File</TableHead>
                      <TableHead>Caricato il</TableHead>
                      <TableHead className="text-right">Azioni</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {f24Documents.map((doc) => (
                      <TableRow key={doc.id}>
                        <TableCell>
                          <Badge variant="secondary">{doc.f24_type}</Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">
                            {doc.f24_month?.toString().padStart(2, '0')}/{doc.f24_year}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm">{doc.description || '-'}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">{doc.file_name}</TableCell>
                        <TableCell>{formatDate(doc.created_at)}</TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="sm" onClick={() => downloadDocument(doc.id)}>
                            <Eye className="h-4 w-4 mr-1" />
                            Visualizza
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Receipt className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Nessun F24 per il {selectedYear}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Upload Payslip Dialog */}
      <Dialog open={payslipDialogOpen} onOpenChange={setPayslipDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Carica Busta Paga</DialogTitle>
            <DialogDescription>
              {selectedStaff && `Dipendente: ${selectedStaff.first_name} ${selectedStaff.last_name}`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Mese</Label>
                <Select 
                  value={payslipForm.month.toString()} 
                  onValueChange={(v) => setPayslipForm({...payslipForm, month: parseInt(v)})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => (
                      <SelectItem key={m} value={m.toString()}>
                        {new Date(2025, m-1, 1).toLocaleDateString('it-IT', {month: 'long'})}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Anno</Label>
                <Select 
                  value={payslipForm.year.toString()} 
                  onValueChange={(v) => setPayslipForm({...payslipForm, year: parseInt(v)})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[2024, 2025, 2026].map(y => (
                      <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>File PDF</Label>
              <Input
                type="file"
                accept=".pdf"
                onChange={(e) => setPayslipForm({...payslipForm, file: e.target.files[0]})}
              />
              <p className="text-xs text-muted-foreground">
                Il dipendente riceverà una notifica automatica e potrà visualizzare il documento.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayslipDialogOpen(false)}>Annulla</Button>
            <Button onClick={handleUploadPayslip} disabled={uploading || !payslipForm.file}>
              {uploading && <RefreshCw className="h-4 w-4 mr-2 animate-spin" />}
              Carica e Invia
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload F24 Dialog */}
      <Dialog open={f24DialogOpen} onOpenChange={setF24DialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Carica F24 Oneri Personale</DialogTitle>
            <DialogDescription>
              Carica il modello F24 per gli oneri del personale
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Mese</Label>
                <Select 
                  value={f24Form.month.toString()} 
                  onValueChange={(v) => setF24Form({...f24Form, month: parseInt(v)})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => (
                      <SelectItem key={m} value={m.toString()}>
                        {new Date(2025, m-1, 1).toLocaleDateString('it-IT', {month: 'short'})}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Anno</Label>
                <Select 
                  value={f24Form.year.toString()} 
                  onValueChange={(v) => setF24Form({...f24Form, year: parseInt(v)})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[2024, 2025, 2026].map(y => (
                      <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Tipo</Label>
                <Select 
                  value={f24Form.f24_type} 
                  onValueChange={(v) => setF24Form({...f24Form, f24_type: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {F24_TYPES.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Descrizione (opzionale)</Label>
              <Textarea
                value={f24Form.description}
                onChange={(e) => setF24Form({...f24Form, description: e.target.value})}
                placeholder="Es: F24 contributi INPS gennaio 2025"
                rows={2}
              />
            </div>
            <div className="space-y-2">
              <Label>File PDF</Label>
              <Input
                type="file"
                accept=".pdf"
                onChange={(e) => setF24Form({...f24Form, file: e.target.files[0]})}
              />
              <p className="text-xs text-muted-foreground">
                Il documento sarà visibile solo ad amministratore e consulente del lavoro.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setF24DialogOpen(false)}>Annulla</Button>
            <Button onClick={handleUploadF24} disabled={uploading || !f24Form.file}>
              {uploading && <RefreshCw className="h-4 w-4 mr-2 animate-spin" />}
              Carica
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
